import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { loginWithPasskey as runPasskeyLogin } from '../lib/passkey';
import { apiErrorMessage } from '../lib/apiError';

const CustomerAuthContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const CustomerAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('mr_customer_token'));
  const [email, setEmail] = useState(() => localStorage.getItem('mr_customer_email'));
  const [name, setName] = useState(() => localStorage.getItem('mr_customer_name') || '');
  const [pricingTier, setPricingTier] = useState(() => localStorage.getItem('mr_customer_tier') || 'retail');
  const [discountPct, setDiscountPct] = useState(() => {
    const v = Number(localStorage.getItem('mr_customer_discount_pct') || '0');
    return Number.isFinite(v) ? v : 0;
  });
  // Per-product manual price overrides set by admin for this customer.
  // Persisted in localStorage so the storefront can render correct prices
  // immediately on page load (before the /customer/me round-trip finishes).
  const [priceOverrides, setPriceOverrides] = useState(() => {
    try {
      const raw = localStorage.getItem('mr_customer_price_overrides');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    if (token) localStorage.setItem('mr_customer_token', token);
    else localStorage.removeItem('mr_customer_token');
  }, [token]);

  useEffect(() => {
    if (email) localStorage.setItem('mr_customer_email', email);
    else localStorage.removeItem('mr_customer_email');
  }, [email]);

  useEffect(() => {
    if (name) localStorage.setItem('mr_customer_name', name);
    else localStorage.removeItem('mr_customer_name');
  }, [name]);

  // Fetch profile (name + pricing tier) whenever token changes
  const fetchProfile = useCallback(async (authToken) => {
    try {
      const { data } = await axios.get(`${API}/customer/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (data.name) setName(data.name);
      const tier = data.pricing_tier || 'retail';
      const pct = Number(data.discount_pct || 0);
      const overrides = data.price_overrides && typeof data.price_overrides === 'object' ? data.price_overrides : {};
      setPricingTier(tier);
      setDiscountPct(pct);
      setPriceOverrides(overrides);
      try {
        localStorage.setItem('mr_customer_tier', tier);
        localStorage.setItem('mr_customer_discount_pct', String(pct));
        localStorage.setItem('mr_customer_price_overrides', JSON.stringify(overrides));
      } catch (e) { /* ignore */ }
    } catch {
      // ignore
    }
  }, []);

  // Capture browser geolocation (high accuracy) and POST to backend
  const captureLocation = useCallback(async (authToken) => {
    if (!('geolocation' in navigator)) return;
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            await axios.post(
              `${API}/customer/location`,
              {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
              },
              { headers: { Authorization: `Bearer ${authToken}` } },
            );
            localStorage.setItem(
              'mr_customer_loc',
              JSON.stringify({
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
              }),
            );
          } catch {
            // best-effort: ignore failures
          } finally {
            resolve();
          }
        },
        () => resolve(), // permission denied / unavailable — silently skip
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
      );
    });
  }, []);

  useEffect(() => {
    if (token) fetchProfile(token);
  }, [token, fetchProfile]);

  const requestOtp = async (loginEmail) => {
    try {
      await axios.post(`${API}/auth/request-otp`, { email: loginEmail });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: apiErrorMessage(err, 'Failed to send code') };
    }
  };

  const verifyOtp = async (loginEmail, code) => {
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, { email: loginEmail, code });
      setToken(data.access_token);
      setEmail(data.email);
      // Fetch the name from /me after sign-in
      await fetchProfile(data.access_token);
      // Capture browser geolocation (one-shot, high accuracy). Permission prompt happens here.
      captureLocation(data.access_token);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: apiErrorMessage(err, 'Invalid code') };
    }
  };

  // ----- SMS-based 4-digit OTP login (mirrors email OTP but via D7 SMS) -----
  // Requires admin to have flipped the customer toggle in
  // Admin → Settings → Notifications → SMS OTP login. Email + phone must both
  // match an existing customer profile — there is no account creation here.
  const requestSmsOtp = async (loginEmail, phone) => {
    try {
      const { data } = await axios.post(`${API}/auth/request-sms-otp`, {
        email: loginEmail,
        phone,
        role: 'customer',
      });
      return { ok: true, masked_phone: data?.masked_phone };
    } catch (err) {
      return { ok: false, error: apiErrorMessage(err, 'Failed to send code') };
    }
  };

  const verifySmsOtp = async (loginEmail, code) => {
    try {
      const { data } = await axios.post(`${API}/auth/verify-sms-otp`, { email: loginEmail, code });
      setToken(data.access_token);
      setEmail(data.email);
      await fetchProfile(data.access_token);
      captureLocation(data.access_token);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: apiErrorMessage(err, 'Invalid code') };
    }
  };

  const signInWithPasskey = async (loginEmail) => {
    try {
      const data = await runPasskeyLogin(loginEmail);
      setToken(data.access_token);
      setEmail(data.email);
      await fetchProfile(data.access_token);
      captureLocation(data.access_token);
      return { ok: true };
    } catch (err) {
      return {
        ok: false,
        error: err.response?.data?.detail || err.message || 'Biometric sign-in failed',
      };
    }
  };

  const logout = () => {
    setToken(null);
    setEmail(null);
    setName('');
    setPricingTier('retail');
    setDiscountPct(0);
    setPriceOverrides({});
    localStorage.removeItem('mr_customer_loc');
    localStorage.removeItem('mr_customer_tier');
    localStorage.removeItem('mr_customer_discount_pct');
    localStorage.removeItem('mr_customer_price_overrides');
  };

  // Derive a friendly display name: real name > capitalized email prefix
  const displayName = (() => {
    if (name) return name.split(' ')[0]; // first name only
    if (email) {
      const prefix = email.split('@')[0].replace(/[._-]/g, ' ');
      return prefix.charAt(0).toUpperCase() + prefix.slice(1);
    }
    return '';
  })();

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  return (
    <CustomerAuthContext.Provider
      value={{ token, email, name, displayName, pricingTier, discountPct, priceOverrides, requestOtp, verifyOtp, requestSmsOtp, verifySmsOtp, signInWithPasskey, logout, authHeaders }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => useContext(CustomerAuthContext);
